import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import "../../Styles/Development.css";
import { developmentWorksAPI } from "../../api/index";

const num = (v) => (typeof v === "number" ? v : Number(v) || 0);

const extractList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || d?.data || [];
};

const extractTotalCount = (res, fallbackLen) => {
  const d = res?.data;
  return num(d?.total ?? d?.count ?? d?.total_count ?? fallbackLen);
};

// TEMPORARY client-side guess for mandal_id -> name. DevelopmentWorkOut only
// returns mandal_id (int), not a name, and there is no GET /mandals endpoint
// in this API. CONFIRM the real IDs with backend before relying on this in
// production — this assumes IDs 1-5 map to these mandals in this exact order.
const MANDAL_ID_MAP = {
  1: "Kandukur",
  2: "Lingasamudram",
  3: "Gudluru",
  4: "Ulavapadu",
  5: "Voletivaripalem",
};

// Normalizes an API row into the shape this component's UI expects.
const normalizeRow = (row) => ({
  id: row.id,
  title: row.title || "Untitled",
  desc: row.description || "",
  mandalId: row.mandal_id,
  mandal: MANDAL_ID_MAP[row.mandal_id] || `Mandal #${row.mandal_id}`,
  villageId: row.village_id,
  category: row.category || "—",
  status: row.status || "pending", // pending | in_progress | completed
  estimated_cost: row.estimated_cost ?? null,
  date: row.work_date || "",
});

// Matches the backend's exact CheckConstraint on development_works.status
const STATUS_OPTIONS = ["pending", "in_progress", "completed"];
const STATUS_LABEL = { pending: "Proposed", in_progress: "Ongoing", completed: "Completed" };

// ---------- Excel template settings ----------
// CONFIRM these column names with the backend's bulk-upload parser.
// If the backend expects different headings, change them here only.
const TEMPLATE_HEADERS = [
  "Mandal / Area",
  "Category",
  "Village",
  "Development Title",
  "Estimated Cost (₹)",
  "Status",
  "Work Date",
  "Description",
];

const TEMPLATE_COLUMN_WIDTHS = [
  { wch: 18 }, // Mandal / Area
  { wch: 18 }, // Category
  { wch: 22 }, // Village
  { wch: 32 }, // Development Title
  { wch: 20 }, // Estimated Cost
  { wch: 14 }, // Status
  { wch: 14 }, // Work Date
  { wch: 45 }, // Description
];

const TEMPLATE_INSTRUCTIONS = [
  ["Column", "Required", "Allowed values / Rules"],
  ["Mandal / Area", "Yes", Object.values(MANDAL_ID_MAP).join(", ")],
  ["Category", "Yes", "Road Works, Water Supply, Drainage"],
  ["Village", "Yes", "Alphabets and spaces only (2-100 characters)"],
  ["Development Title", "Yes", "3-150 characters"],
  ["Estimated Cost (₹)", "Yes", "Numbers only, greater than 0 (e.g. 250000 or 250000.50)"],
  ["Status", "Yes", "pending, in_progress, completed"],
  ["Work Date", "Yes", "Format: YYYY-MM-DD (e.g. 2026-10-03)"],
  ["Description", "No", "Optional. If filled: 10-1000 characters"],
];

const Development = () => {
  const navigate = useNavigate();

  const [developments, setDevelopments] = useState([]);
  const [loading, setLoading] = useState(true);

  // GET /development-works/status-summary returns list[{status, total}] —
  // normalized here into a { pending: n, in_progress: n, completed: n } map
  const [statusCounts, setStatusCounts] = useState({});

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All Categories");
  const [status, setStatus] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 10;

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileInputRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const getIconClass = (cat) => {
    switch (cat) {
      case "Road Works": return "dev123-icon-road";
      case "Drainage": return "dev123-icon-drainage";
      case "Water Supply": return "dev123-icon-water";
      default: return "dev123-icon-road";
    }
  };

  const getCategoryClass = (cat) => {
    switch (cat) {
      case "Road Works": return "dev123-cat-road-works";
      case "Drainage": return "dev123-cat-drainage";
      case "Water Supply": return "dev123-cat-water-project";
      default: return "";
    }
  };

  const getStatusClass = (st) => {
    switch (st) {
      case "completed": return "dev123-status-completed";
      case "in_progress": return "dev123-status-ongoing";
      case "pending": return "dev123-status-proposed";
      default: return "";
    }
  };

  /* ---------- Fetch list (filters + pagination as query params) ---------- */
  const fetchDevelopments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: PAGE_SIZE };
      if (search.trim()) params.search = search.trim();
      if (category !== "All Categories") params.category = category;
      if (status !== "All Status") params.status = status;

      const res = await developmentWorksAPI.getAll(params);
      const rows = extractList(res).map(normalizeRow);
      setDevelopments(rows);
      setTotalCount(extractTotalCount(res, rows.length));
    } catch (err) {
      toast.error("Could not load development works");
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, category, status]);

  // list[{status, total}] -> { pending: n, in_progress: n, completed: n }
  const fetchStatusSummary = useCallback(async () => {
    try {
      const res = await developmentWorksAPI.statusSummary();
      const rows = Array.isArray(res.data) ? res.data : [];
      const map = {};
      rows.forEach((r) => { map[r.status] = num(r.total); });
      setStatusCounts(map);
    } catch (err) {
      // Non-fatal — stat cards fall back to counting the current page
    }
  }, []);

  useEffect(() => { fetchDevelopments(); }, [fetchDevelopments]);
  useEffect(() => { fetchStatusSummary(); }, [fetchStatusSummary]);

  const resetFilters = () => {
    setSearch("");
    setCategory("All Categories");
    setStatus("All Status");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  /* ---------- Edit ---------- */
  const openEditModal = (item) => {
    setEditItem({ ...item, mandalName: MANDAL_ID_MAP[item.mandalId] || "" });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await developmentWorksAPI.update(editItem.id, {
        title: editItem.title,
        description: editItem.desc,
        mandal_name: editItem.mandalName,
        category: editItem.category,
        status: editItem.status,
        estimated_cost: editItem.estimated_cost ? Number(editItem.estimated_cost) : undefined,
        work_date: editItem.date || undefined,
      });
      toast.success("Development work updated");
      closeEditModal();
      fetchDevelopments();
      fetchStatusSummary();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail :
        Array.isArray(detail) ? detail.map((d) => d.reason || d.msg).join("; ") :
        "Update failed"
      );
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Delete ---------- */
  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await developmentWorksAPI.delete(deleteId);
      toast.success("Deleted successfully");
      closeDeleteModal();
      fetchDevelopments();
      fetchStatusSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed (super_admin only)");
    } finally {
      setDeleting(false);
    }
  };

  /* ---------- Download Excel template (headings only) ---------- */
  const handleDownloadTemplate = () => {
    try {
      // Sheet 1: Template (headings only, ready to fill)
      const templateSheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
      templateSheet["!cols"] = TEMPLATE_COLUMN_WIDTHS;

      // Sheet 2: Instructions (allowed values and rules)
      const instructionSheet = XLSX.utils.aoa_to_sheet(TEMPLATE_INSTRUCTIONS);
      instructionSheet["!cols"] = [{ wch: 22 }, { wch: 10 }, { wch: 70 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, templateSheet, "Template");
      XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");

      XLSX.writeFile(workbook, "Development_Works_Template.xlsx");
      toast.success("Template downloaded successfully");
    } catch (err) {
      console.error("Template download failed:", err);
      toast.error("Could not download the template");
    }
  };

  /* ---------- Bulk upload — all-or-nothing per backend design ---------- */
  const handleBulkUploadClick = () => bulkFileInputRef.current?.click();

  const handleBulkFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    setBulkUploading(true);
    try {
      const res = await developmentWorksAPI.bulkUpload(file);
      // Success shape: { inserted: N, errors: [] }
      toast.success(`Import complete — ${num(res.data?.inserted)} development works added`);
      fetchDevelopments();
      fetchStatusSummary();
    } catch (err) {
      // All-or-nothing: a 422 here means NONE of the rows were inserted.
      // detail = list of { row, reason } (capped_error_detail).
      const detail = err.response?.data?.detail;
      console.error("Bulk upload rejected — no rows inserted:", detail);

      if (Array.isArray(detail)) {
        const preview = detail.slice(0, 3).map((d) => `Row ${d.row}: ${d.reason}`).join(" | ");
        toast.error(`Import failed (${detail.length} row error${detail.length > 1 ? "s" : ""}) — ${preview}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Bulk upload failed — check console for details");
      }
    } finally {
      setBulkUploading(false);
    }
  };

  /* ---------- Stat cards — derived from statusCounts map ---------- */
  const completed = statusCounts.completed || 0;
  const ongoing = statusCounts.in_progress || 0;
  const proposed = statusCounts.pending || 0;
  const totalProjects = completed + ongoing + proposed || totalCount;

  return (
    <div className="dev123-page">
      <input
        ref={bulkFileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleBulkFileSelected}
      />

      {/* Header */}
      <div className="dev123-header">
        <div className="dev123-header-left">
          <div className="dev123-page-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 21h20L12 2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div>
            <h2 className="dev123-title">Development Works</h2>
            <p className="dev123-subtitle">Track and manage all development projects and works</p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="dev123-add-btn"
            onClick={handleDownloadTemplate}
            style={{ background: "#16a34a" }}
          >
            <span className="dev123-add-icon">⬇</span>
            Download Template
          </button>
          <button className="dev123-add-btn" onClick={handleBulkUploadClick} disabled={bulkUploading}
            style={{ background: "#0b3d24" }}>
            <span className="dev123-add-icon">{bulkUploading ? "⏳" : "⬆"}</span>
            {bulkUploading ? "Uploading..." : "Bulk Upload (Excel)"}
          </button>
          <button className="dev123-add-btn" onClick={() => navigate("/dashboard/development-form")}>
            <span className="dev123-add-icon">+</span> Add Development Work
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="dev123-stats-grid">
        <div className="dev123-stat-card">
          <div className="dev123-stat-icon dev123-stat-icon-green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="dev123-stat-info">
            <span className="dev123-stat-label">Total Projects</span>
            <span className="dev123-stat-value">{totalProjects}</span>
            <span className="dev123-stat-sub">All time records</span>
          </div>
        </div>

        <div className="dev123-stat-card">
          <div className="dev123-stat-icon dev123-stat-icon-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="dev123-stat-info">
            <span className="dev123-stat-label">Completed</span>
            <span className="dev123-stat-value">{completed}</span>
            <span className="dev123-stat-sub">
              {totalProjects ? ((completed / totalProjects) * 100).toFixed(1) : 0}% of total
            </span>
          </div>
        </div>

        <div className="dev123-stat-card">
          <div className="dev123-stat-icon dev123-stat-icon-orange">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="dev123-stat-info">
            <span className="dev123-stat-label">Ongoing</span>
            <span className="dev123-stat-value">{ongoing}</span>
            <span className="dev123-stat-sub">
              {totalProjects ? ((ongoing / totalProjects) * 100).toFixed(1) : 0}% of total
            </span>
          </div>
        </div>

        <div className="dev123-stat-card">
          <div className="dev123-stat-icon dev123-stat-icon-purple">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M7 3h7l4 4v14a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M14 3v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="dev123-stat-info">
            <span className="dev123-stat-label">Proposed</span>
            <span className="dev123-stat-value">{proposed}</span>
            <span className="dev123-stat-sub">
              {totalProjects ? ((proposed / totalProjects) * 100).toFixed(1) : 0}% of total
            </span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="dev123-filters">
        <div className="dev123-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search Development Work..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          />
        </div>

        <select className="dev123-select" value={category} onChange={(e) => { setCategory(e.target.value); setCurrentPage(1); }}>
          <option>All Categories</option>
          <option>Road Works</option>
          <option>Drainage</option>
          <option>Water Supply</option>
        </select>

        <select className="dev123-select" value={status} onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}>
          <option value="All Status">All Status</option>
          <option value="completed">Completed</option>
          <option value="in_progress">Ongoing</option>
          <option value="pending">Proposed</option>
        </select>

        <button className="dev123-reset-btn" onClick={resetFilters}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M4 4v6h6M20 20v-6h-6M4.5 15a8 8 0 0014.5 3.5M19.5 9A8 8 0 005 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Reset Filters
        </button>
      </div>

      {/* Table */}
      <div className="dev123-table-wrapper">
        <table className="dev123-table table table-hover">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Title</th>
              <th>Mandal</th>
              <th>Category</th>
              <th>Status</th>
              <th>Date</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="dev123-no-data">Loading…</td></tr>
            ) : developments.length === 0 ? (
              <tr><td colSpan="7" className="dev123-no-data">No development works found.</td></tr>
            ) : (
              developments.map((item, index) => (
                <tr key={item.id}>
                  <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>

                  <td>
                    <div className="dev123-title-cell">
                      <div className={`dev123-title-icon ${getIconClass(item.category)}`}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2L2 21h20L12 2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <div>
                        <div className="dev123-title-text">{item.title}</div>
                        <div className="dev123-title-desc">{item.desc}</div>
                      </div>
                    </div>
                  </td>

                  <td>{item.mandal}</td>

                  <td>
                    <span className={`dev123-category-badge ${getCategoryClass(item.category)}`}>{item.category}</span>
                  </td>

                  <td>
                    <span className={`dev123-status-badge ${getStatusClass(item.status)}`}>{STATUS_LABEL[item.status] || item.status}</span>
                  </td>

                  <td>
                    <div className="dev123-date-cell">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                      {item.date}
                    </div>
                  </td>

                  <td>
                    <button className="dev123-edit-btn" onClick={() => openEditModal(item)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Edit
                    </button>
                    <button className="dev123-delete-btn" onClick={() => openDeleteModal(item.id)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="dev123-pagination-bar">
        <span className="dev123-pagination-info">
          Showing {developments.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0}–{(currentPage - 1) * PAGE_SIZE + developments.length} of {totalCount} entries
        </span>

        <div className="dev123-pagination-controls">
          <button className="dev123-page-arrow" onClick={() => handlePageChange(currentPage - 1)}>&lt;</button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let p = i + 1;
            if (totalPages > 5 && currentPage > 3) p = currentPage - 2 + i;
            if (p > totalPages) return null;
            return (
              <button key={p} className={`dev123-page-num ${currentPage === p ? "dev123-active" : ""}`} onClick={() => handlePageChange(p)}>
                {p}
              </button>
            );
          })}
          <button className="dev123-page-arrow" onClick={() => handlePageChange(currentPage + 1)}>&gt;</button>
        </div>
      </div>

      {/* EDIT MODAL */}
      {showEditModal && editItem && (
        <div className="dev123-modal-overlay" onClick={closeEditModal}>
          <div className="dev123-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dev123-modal-header">
              <h3>Edit Development Work</h3>
              <button className="dev123-modal-close" onClick={closeEditModal}>&times;</button>
            </div>

            <form className="dev123-modal-body" onSubmit={handleEditSave}>
              <div className="dev123-form-group">
                <label>Title</label>
                <input type="text" name="title" value={editItem.title} onChange={handleEditChange} required />
              </div>

              <div className="dev123-form-group">
                <label>Description</label>
                <textarea name="desc" value={editItem.desc} onChange={handleEditChange} rows="3" />
              </div>

              <div className="dev123-form-row">
                <div className="dev123-form-group">
                  <label>Mandal</label>
                  <select name="mandalName" value={editItem.mandalName} onChange={handleEditChange} required>
                    <option value="">Select Mandal</option>
                    {Object.values(MANDAL_ID_MAP).map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="dev123-form-group">
                  <label>Category</label>
                  <select name="category" value={editItem.category} onChange={handleEditChange}>
                    <option>Road Works</option>
                    <option>Drainage</option>
                    <option>Water Supply</option>
                  </select>
                </div>
              </div>

              <div className="dev123-form-row">
                <div className="dev123-form-group">
                  <label>Status</label>
                  <select name="status" value={editItem.status} onChange={handleEditChange}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
                <div className="dev123-form-group">
                  <label>Date</label>
                  <input type="date" name="date" value={editItem.date} onChange={handleEditChange} />
                </div>
              </div>

              <div className="dev123-form-group">
                <label>Estimated Cost</label>
                <input type="number" name="estimated_cost" value={editItem.estimated_cost || ""} onChange={handleEditChange} />
              </div>

              <div className="dev123-modal-footer">
                <button type="button" className="dev123-btn-cancel" onClick={closeEditModal}>Cancel</button>
                <button type="submit" className="dev123-btn-save" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {showDeleteModal && (
        <div className="dev123-modal-overlay" onClick={closeDeleteModal}>
          <div className="dev123-modal dev123-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dev123-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="dev123-confirm-title">Delete this record?</h3>
            <p className="dev123-confirm-text">
              This action cannot be undone. Are you sure you want to delete this development work?
            </p>
            <div className="dev123-confirm-footer">
              <button className="dev123-btn-cancel" onClick={closeDeleteModal}>Cancel</button>
              <button className="dev123-btn-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "OK, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Development;